import React, { useCallback, useMemo, useState } from 'react';
import { getIn } from 'formik';
import { makeStyles } from '@material-ui/core/styles';
import Grid from '@material-ui/core/Grid';

import { FieldElementPropTypes } from '../propTypes';
import { TextField } from 'core/components/text-field';
import { Autocomplete } from 'core/components/autocomplete';
import { FormField } from 'core/components/form';
import { Label } from 'core/components/label';
import {
  createMortgageLenderSearchIndex,
  searchMortgageLenders
} from 'common/components/mortgage-details/search-mortgage-lenders';

const CUSTOM_ID = 'isCustom';
const CUSTOM_OPTION_LABEL = 'Add a lender not listed in our database';

const useStyles = makeStyles((theme) => ({
  address: {
    display: 'block',
    marginTop: theme.spacing(1)
  },
  customOption: {
    fontWeight: 600,
    fontSize: 12,
    lineHeight: '16px',
    color: theme.colorPalette.green_90
  },
  addLenderText: {
    fontWeight: 400,
    color: theme.colorPalette.orange_50
  }
}));

const getFieldIdPrefix = (id) => {
  const lastPeriodIndex = id.lastIndexOf('.');
  // Separate the prefix and the last part
  const prefixFieldId = id.slice(0, lastPeriodIndex + 1);
  return prefixFieldId;
};

// AddMortgage's field contract: the lender-name field has sibling `id` and `isCustom`
// fields. `onSelection` writes or clears that directory ID and its billing address;
// `mortgageHolderId` is populated separately when the primary mortgage is mirrored.
const MortgageLenderNameAutocomplete = ({
  field: { id, name, label, mode, options, value, onSelection, helperText, onCustomSelection, ...props },
  ctx: { setFieldValue, touched, errors, classes, setFieldTouched, values }
}) => {
  const customStyles = useStyles();
  const [inputValue, setInputValue] = useState(value || '');
  const [isCustom, setIsCustom] = useState(() => {
    return value ? !options.some((o) => o.id === value || o.text === value) : false;
  });
  const prefixFieldId = getFieldIdPrefix(id);
  const isCustomFieldId = `${prefixFieldId}${CUSTOM_ID}`;

  const fieldId = isCustom ? isCustomFieldId : id;
  const customOption = { id: isCustomFieldId, text: CUSTOM_OPTION_LABEL, isCustom: true };

  const handleSelectChange = useCallback(
    (_event, val) => {
      setFieldTouched(id, true, false);
      setInputValue(val?.text || '');
      const isCustomOptionSelected = !!val?.isCustom;
      setIsCustom(isCustomOptionSelected);
      if (isCustomOptionSelected) {
        setFieldValue(id, '');
        setFieldValue(`${prefixFieldId}id`, null);
        setFieldValue(`${prefixFieldId}isCustom`, true);
        onCustomSelection?.();
      } else {
        setFieldValue(id, val?.text || '');
        setFieldValue(`${prefixFieldId}isCustom`, false);
        onSelection?.(id, val);
      }
    },
    [setFieldTouched, id, setFieldValue, onSelection, onCustomSelection, prefixFieldId]
  );

  const handleInputChange = useCallback(
    (_event, nextInputValue, reason) => {
      // Selection and blur manage their labels explicitly; a reset after clearing the old
      // selection while typing must not erase the new search query.
      if (reason === 'reset') {
        return;
      }
      setInputValue(nextInputValue);
      if (reason === 'input') {
        setIsCustom(false);
        setFieldTouched(id, true, false);
        setFieldValue(id, '');
        setFieldValue(isCustomFieldId, false);
        // Editing a selection must not retain its directory ID or billing address.
        if (value) {
          onSelection?.(id, null);
        }
      }
    },
    [id, isCustomFieldId, value, setFieldTouched, setFieldValue, onSelection]
  );

  const handleBlur = useCallback(
    (event) => {
      const {
        target: { value }
      } = event;
      setFieldTouched(id, true, false);
      setFieldValue(id, value);
    },
    [setFieldTouched, id, setFieldValue]
  );

  const searchIndex = useMemo(() => createMortgageLenderSearchIndex(options), [options]);
  const filteredOptions = useMemo(() => searchMortgageLenders(searchIndex, inputValue), [searchIndex, inputValue]);
  const selectedId = getIn(values, `${prefixFieldId}id`);
  // Entries without a directory id carry their array index as `id`, which shifts when the
  // directory refreshes; fall back to the name so an earlier selection survives the swap.
  const currentValue = isCustom
    ? customOption
    : options.find((option) => option.id === selectedId && option.text === value) ||
      options.find((option) => option.text === value) ||
      null;

  return (
    <>
      <Autocomplete
        {...props}
        value={currentValue}
        inputValue={inputValue}
        onInputChange={handleInputChange}
        freeSolo={false}
        filterOptions={(options) => options}
        getOptionSelected={(option, selected) => option.id === selected.id}
        classes={classes}
        name={fieldId}
        id={fieldId}
        options={filteredOptions.concat(customOption)}
        onSelection={handleSelectChange}
        onBlur={() => {
          setFieldTouched(id, true, false);
          setInputValue(currentValue?.text || '');

          if (!currentValue) {
            setFieldValue(id, '');
          }
        }}
        renderOption={(option) => {
          return option.isCustom ? (
            <div className={customStyles.customOption}>
              Can't find your lender?{' '}
              <span className={customStyles.addLenderText}>Add a lender not listed in our database</span>
            </div>
          ) : (
            <div>
              {option.text}
              {option.addressText && (
                <Label type="body4" className={customStyles.address}>
                  {option.addressText}
                </Label>
              )}
            </div>
          );
        }}
        errors={errors}
        touched={touched}
        InputComponent={TextField}
        inputProps={{
          label,
          mode,
          error: getIn(touched, fieldId) && !!getIn(errors, fieldId),
          helperText: getIn(touched, fieldId) ? getIn(errors, fieldId) : helperText || undefined,
          disabled: props.disabled
        }}
      />
      {isCustom && (
        <Grid container justifyContent="space-around" alignItems="flex-start">
          <FormField
            mode={mode}
            id={id}
            name={id}
            label="Lender name"
            type="string"
            xs={12}
            permissions={{ isLicensedAction: false }}
            onBlur={handleBlur}
          />
        </Grid>
      )}
    </>
  );
};

MortgageLenderNameAutocomplete.propTypes = FieldElementPropTypes;

export default MortgageLenderNameAutocomplete;
